"use client";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { languageName, resolveInitialLang } from "./resolve";

export { languageName, resolveInitialLang } from "./resolve";

// ---------------------------------------------------------------------------
// Kleines, typsicheres UI-i18n OHNE Dependencies — der Mechanismus wohnt hier,
// die Wörterbücher wohnen in der jeweiligen App (z. B. agenda/src/shared/i18n/).
//
// Prinzip: EIN Wörterbuch-Objekt pro Sprache; die Referenzsprache definiert den
// Typ (`type Dict = typeof de`), alle weiteren Sprachen müssen ihn erfüllen —
// fehlende oder überzählige Schlüssel sind damit COMPILE-Fehler, keine
// Laufzeit-Fallbacks. Plural/Interpolation sind schlicht Funktionen im
// Wörterbuch (`(n) => ...`): voll typsicher, und jede Sprache bringt ihre
// eigene Plural-Logik mit.
//
// EAGER ODER LAZY — die App entscheidet je Sprache (siehe `DictSource`):
//   dictionaries: { de, es }                              // eager, im Bundle
//   dictionaries: { de: () => import("./de").then(m => m.de) }  // eigener Chunk
// Beides mischbar. Kleine Wörterbücher (agenda) bleiben sinnvoll eager, große
// (admin: 5 Sprachen × ~40 KB) gehören in eigene Chunks — sonst lädt jeder
// Nutzer vier Sprachen mit, die er nie sieht.
// ---------------------------------------------------------------------------

/**
 * Ein Wörterbuch — entweder direkt (eager) oder als Lader (lazy).
 *
 * Unterschieden wird über `typeof === "function"`; Wörterbücher sind immer
 * Objekte, ein Konflikt ist also ausgeschlossen.
 */
export type DictSource<D> = D | (() => Promise<D> | D);

export type I18nConfig<D> = {
  /** Sprachcode → Wörterbuch oder Lader. Die Schlüssel sind die verfügbaren Sprachen. */
  dictionaries: Record<string, DictSource<D>>;
  /** Sprache, wenn weder Speicher noch Browser etwas Passendes liefern. */
  fallback: string;
  /** localStorage-Schlüssel der Nutzerwahl (App-spezifisch, z. B. "agenda.lang"). */
  storageKey: string;
  /**
   * Was gerendert wird, solange das ERSTE Wörterbuch lädt (nur bei Lazy; bei
   * eager gibt es diesen Zustand nicht). Default: nichts.
   *
   * Beim UMSCHALTEN greift das bewusst nicht — dort bleibt die alte Sprache
   * stehen, bis die neue da ist. Ein Aufblitzen von Leere mitten in der Arbeit
   * wäre schlimmer als ein Sekundenbruchteil alter Beschriftungen.
   */
  pending?: ReactNode;
};

export type I18n<D> = {
  I18nProvider: (props: { children: ReactNode }) => ReactNode;
  /** Aktive Sprache + Umschalten + verfügbare Sprachen (für den Switcher). */
  useI18n: () => {
    lang: string;
    setLang: (lang: string) => void;
    available: string[];
  };
  /** Das komplette Wörterbuch der aktiven Sprache — `t.tabs.list` usw. */
  useT: () => D;
};

/** Erzeugt Provider + Hooks für die Wörterbücher EINER App. */
export function createI18n<D>(config: I18nConfig<D>): I18n<D> {
  const available = Object.keys(config.dictionaries);

  // Einmal geladene Wörterbücher bleiben hier: Zurückschalten ist dann sofort
  // und ohne Zwischenzustand. Modul-Ebene, nicht im Provider — ein Remount des
  // Providers soll nicht neu laden.
  const cache = new Map<string, D>();

  const Ctx = createContext<{
    lang: string;
    setLang: (l: string) => void;
    dict: D;
  } | null>(null);

  /** Sofort verfügbares Wörterbuch (eager registriert oder schon geladen). */
  function readSync(lang: string): D | undefined {
    const hit = cache.get(lang);
    if (hit) return hit;
    const src = config.dictionaries[lang];
    if (src === undefined || typeof src === "function") return undefined;
    cache.set(lang, src);
    return src;
  }

  async function load(lang: string): Promise<D> {
    const hit = readSync(lang);
    if (hit) return hit;
    const src = config.dictionaries[lang] as () => Promise<D> | D;
    const dict = await src();
    cache.set(lang, dict);
    return dict;
  }

  function initialLang(): string {
    let stored: string | null = null;
    try {
      stored = window.localStorage.getItem(config.storageKey);
    } catch {
      // localStorage kann fehlen/gesperrt sein (SSR, Private Mode) — dann Browser/Fallback.
    }
    const navLangs =
      typeof navigator !== "undefined"
        ? (navigator.languages ?? [navigator.language])
        : [];
    return resolveInitialLang(available, stored, navLangs, config.fallback);
  }

  function I18nProvider({ children }: { children: ReactNode }) {
    const [lang, setLangState] = useState(initialLang);
    // Bei eager registrierten Sprachen ist das Wörterbuch sofort da und es gibt
    // nie einen Ladezustand — der Lazy-Pfad kostet die anderen Apps also nichts.
    const [dict, setDict] = useState<D | undefined>(() => readSync(lang));

    // Die zuletzt ANGEFORDERTE Sprache. Schaltet jemand schnell zweimal um,
    // darf die langsamere Antwort die neuere nicht überschreiben.
    const wanted = useRef(lang);

    useEffect(() => {
      wanted.current = lang;
      const ready = readSync(lang);
      if (ready) {
        setDict(ready);
        return;
      }
      let cancelled = false;
      void load(lang)
        .then((d) => {
          if (!cancelled && wanted.current === lang) setDict(d);
        })
        .catch((err: unknown) => {
          // Chunk nicht ladbar (Netz, Deploy dazwischen). Die Auswahl
          // zurücknehmen, sonst zeigt der Umschalter eine Sprache als aktiv,
          // die gar nicht anliegt.
          console.error(`[i18n] Wörterbuch "${lang}" nicht ladbar:`, err);
          if (!cancelled && wanted.current === lang) {
            setLangState((prev) => (prev === lang ? config.fallback : prev));
          }
        });
      return () => {
        cancelled = true;
      };
    }, [lang]);

    // <html lang> folgt der TATSÄCHLICH angelegten Sprache (dict geladen) —
    // die SPAs tragen in ihrer index.html nur einen statischen Fallback, und
    // ein falsches lang-Attribut verwirrt Screenreader (falsche Aussprache)
    // und Übersetzungs-Features des Browsers. Zentral hier statt je App.
    useEffect(() => {
      if (dict) document.documentElement.lang = lang;
    }, [lang, dict]);

    const setLang = useCallback((l: string) => {
      if (!available.includes(l)) return;
      setLangState(l);
      try {
        window.localStorage.setItem(config.storageKey, l);
      } catch {
        // nicht persistierbar -> Wahl gilt nur für diese Sitzung
      }
    }, []);

    const value = useMemo(
      () => (dict ? { lang, setLang, dict } : null),
      [lang, setLang, dict],
    );

    // Nur beim allerersten Laden: es gibt noch KEIN Wörterbuch, also auch keine
    // übersetzte Oberfläche, die man zeigen könnte.
    if (!value) return <>{config.pending ?? null}</>;

    return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
  }

  function useI18nCtx() {
    const ctx = useContext(Ctx);
    if (!ctx)
      throw new Error("useI18n/useT brauchen den I18nProvider (App-Wurzel).");
    return ctx;
  }

  return {
    I18nProvider,
    useI18n: () => {
      const { lang, setLang } = useI18nCtx();
      return { lang, setLang, available };
    },
    useT: () => useI18nCtx().dict,
  };
}

/** Anzeige-Einträge für einen Sprachumschalter: Code + Endonym. */
export function languageOptions(
  available: readonly string[],
): { code: string; label: string }[] {
  return available.map((code) => ({ code, label: languageName(code) }));
}
